/**
 * @file Links Service
 * @description API client for investment link operations.
 * Handles CRUD for hyperlinks associated with investments.
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { InvestmentLink } from '@medsuite/models';

/**
 * DTO for creating a new link
 */
export interface CreateLinkDto {
  title: string;
  url: string;
  created_by?: string | null;
}

/**
 * DTO for updating an existing link
 */
export interface UpdateLinkDto {
  title: string;
  url: string;
  modified_by?: string | null;
}

/**
 * Response for list of links
 */
export interface LinksListResponse {
  investment_id: number;
  links: InvestmentLink[];
  total: number;
}

/**
 * Link with optional duplicate warning from server
 */
export interface LinkWithWarning extends InvestmentLink {
  duplicate_warning: string | null;
}

/**
 * Links Service
 */
class LinksService {
  /**
   * Get all links for an investment
   */
  async getAll(investmentId: number): Promise<ApiResponse<LinksListResponse>> {
    return apiClient.get<LinksListResponse>(`/investments/${investmentId}/links`);
  }

  /**
   * Create a new link
   */
  async create(
    investmentId: number,
    data: CreateLinkDto
  ): Promise<ApiResponse<LinkWithWarning>> {
    return apiClient.post<LinkWithWarning>(`/investments/${investmentId}/links`, data);
  }

  /**
   * Update an existing link
   */
  async update(
    investmentId: number,
    linkId: number,
    data: UpdateLinkDto
  ): Promise<ApiResponse<LinkWithWarning>> {
    return apiClient.put<LinkWithWarning>(
      `/investments/${investmentId}/links/${linkId}`,
      data
    );
  }

  /**
   * Delete a link
   */
  async delete(investmentId: number, linkId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${investmentId}/links/${linkId}`);
  }
}

export const linksService = new LinksService();
